import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import api from '../api/client.js';
import { useAuth } from '../auth/AuthContext.jsx';
import clsx from 'clsx';

// ── Helpers ─────────────────────────────────────────────────────────────────

function fmtDate(iso) {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function fmtDateTime(iso) {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

function fmt$(v) {
  const n = Number(v || 0);
  if (n === 0) return null;
  if (n >= 1_000_000) return '$' + (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1000) return '$' + Math.round(n).toLocaleString();
  return '$' + n.toFixed(0);
}

const TOUCH_LABELS = {
  visit: 'Visit', call: 'Call', email: 'Email',
  quote: 'Quote', note: 'Note',
};

const TOUCH_COLORS = {
  visit: 'bg-navy text-white',
  call:  'bg-blue-600 text-white',
  email: 'bg-teal-600 text-white',
  quote: 'bg-crimson text-white',
  note:  'bg-slate-400 text-white',
};

const SENTIMENT_COLORS = {
  positive: 'text-green-700 bg-green-50',
  neutral:  'text-slate-600 bg-slate-100',
  negative: 'text-red-600 bg-red-50',
};

const PERIODS = [
  { key: '30d', label: '30 days' },
  { key: '90d', label: '90 days' },
  { key: 'ytd', label: 'YTD'     },
  { key: '12m', label: '12 mo'   },
];

// ── Summary table ────────────────────────────────────────────────────────────

function SummaryTable({ rows, selected, onSelect }) {
  if (rows.length === 0) {
    return <p className="text-sm text-muted py-6 text-center">No activity in this period.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-navy-100 text-muted font-medium text-left">
            <th className="py-2 pr-4">Date</th>
            <th className="py-2 pr-4">Rep</th>
            <th className="py-2 px-2 text-center">Visits</th>
            <th className="py-2 px-2 text-center">Calls</th>
            <th className="py-2 px-2 text-center">Emails</th>
            <th className="py-2 px-2 text-center">Quotes</th>
            <th className="py-2 px-2 text-center">Notes</th>
            <th className="py-2 px-2 text-center font-semibold text-navy">Total</th>
            <th className="py-2 px-2 text-right">Sale value</th>
            <th className="py-2 pl-4">Customers</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-navy-100">
          {rows.map((r, i) => {
            const key = `${r.rep_id}::${r.day_start}`;
            const isSelected = selected === key;
            const saleValue = fmt$(r.sale_value);
            return (
              <tr
                key={i}
                onClick={() => onSelect(isSelected ? null : key)}
                className={clsx(
                  'cursor-pointer transition-colors',
                  isSelected
                    ? 'bg-navy text-white'
                    : 'hover:bg-panel'
                )}
              >
                <td className={clsx('py-2 pr-4 whitespace-nowrap', isSelected ? 'text-white' : 'text-muted')}>
                  {fmtDate(r.day_start)}
                </td>
                <td className={clsx('py-2 pr-4 font-medium', isSelected ? 'text-white' : 'text-navy')}>
                  {r.rep}
                </td>
                <td className="py-2 px-2 text-center">{r.visit || 0}</td>
                <td className="py-2 px-2 text-center">{r.call || 0}</td>
                <td className="py-2 px-2 text-center">{r.email || 0}</td>
                <td className="py-2 px-2 text-center">{r.quote || 0}</td>
                <td className="py-2 px-2 text-center">{r.note || 0}</td>
                <td className={clsx('py-2 px-2 text-center font-semibold', isSelected ? 'text-white' : 'text-navy')}>
                  {r.total}
                </td>
                <td className={clsx('py-2 px-2 text-right tabular-nums text-xs', isSelected ? 'text-green-300' : 'text-green-700')}>
                  {saleValue || '—'}
                </td>
                <td className={clsx('py-2 pl-4 text-xs max-w-xs', isSelected ? 'text-blue-200' : 'text-muted')}>
                  {(r.customers || []).slice(0, 4).join(', ')}
                  {(r.customers || []).length > 4 && ` +${r.customers.length - 4} more`}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Detail table ─────────────────────────────────────────────────────────────

function DetailTable({ rows }) {
  const [expanded, setExpanded] = useState(null);

  if (rows.length === 0) {
    return <p className="text-sm text-muted py-6 text-center">No records match the current filter.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-navy-100 text-muted font-medium text-left">
            <th className="py-2 pr-3 whitespace-nowrap">Date</th>
            <th className="py-2 pr-3">Rep</th>
            <th className="py-2 pr-3">Customer</th>
            <th className="py-2 pr-3">Contact</th>
            <th className="py-2 pr-3">Type</th>
            <th className="py-2 pr-3">Sale $</th>
            <th className="py-2 pr-3">Topics</th>
            <th className="py-2 pr-3">Sentiment</th>
            <th className="py-2">Actions / Notes</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-navy-100">
          {rows.map(r => {
            const topics      = Array.isArray(r.topics)       ? r.topics       : [];
            const actionItems = Array.isArray(r.action_items) ? r.action_items : [];
            const isOpen      = expanded === r.id;

            return (
              <>
                <tr
                  key={r.id}
                  className="hover:bg-panel cursor-pointer"
                  onClick={() => setExpanded(isOpen ? null : r.id)}
                >
                  <td className="py-2 pr-3 text-muted whitespace-nowrap">{fmtDateTime(r.date)}</td>
                  <td className="py-2 pr-3 font-medium text-navy whitespace-nowrap">{r.rep}</td>
                  <td className="py-2 pr-3 text-navy">
                    {r.customer_id
                      ? <Link to={`/customers/${r.customer_id}`}
                              className="hover:underline"
                              onClick={e => e.stopPropagation()}>
                          {r.customer}
                        </Link>
                      : <span className="text-muted italic">—</span>
                    }
                  </td>
                  <td className="py-2 pr-3 text-sm text-navy">
                    {r.contact_name || <span className="text-muted">—</span>}
                  </td>
                  <td className="py-2 pr-3">
                    <span className={clsx('text-xs px-1.5 py-0.5 rounded font-medium',
                      TOUCH_COLORS[r.touch_type] || 'bg-slate-100 text-slate-600')}>
                      {TOUCH_LABELS[r.touch_type] || r.touch_type}
                    </span>
                  </td>
                  <td className="py-2 pr-3 tabular-nums text-xs text-green-700 whitespace-nowrap">
                    {fmt$(r.sale_value) || '—'}
                  </td>
                  <td className="py-2 pr-3">
                    <div className="flex flex-wrap gap-1">
                      {topics.slice(0, 3).map((t, i) => (
                        <span key={i} className="text-xs bg-panel text-navy px-1.5 py-0.5 rounded">
                          {String(t).replace(/_/g, ' ')}
                        </span>
                      ))}
                      {topics.length > 3 && (
                        <span className="text-xs text-muted">+{topics.length - 3}</span>
                      )}
                    </div>
                  </td>
                  <td className="py-2 pr-3">
                    {r.sentiment && (
                      <span className={clsx('text-xs px-1.5 py-0.5 rounded capitalize',
                        SENTIMENT_COLORS[r.sentiment] || SENTIMENT_COLORS.neutral)}>
                        {r.sentiment}
                      </span>
                    )}
                  </td>
                  <td className="py-2 text-muted text-xs max-w-xs truncate">
                    {actionItems[0]
                      ? String(actionItems[0])
                      : r.snippet
                        ? r.snippet.substring(0, 80) + (r.snippet.length > 80 ? '…' : '')
                        : '—'
                    }
                  </td>
                </tr>
                {isOpen && (
                  <tr key={`${r.id}-expand`} className="bg-panel">
                    <td colSpan={9} className="px-4 py-3">
                      <div className="space-y-2 text-sm">
                        {actionItems.length > 0 && (
                          <div>
                            <span className="font-medium text-navy text-xs uppercase tracking-wider">
                              Action Items
                            </span>
                            <ul className="mt-1 space-y-0.5">
                              {actionItems.map((a, i) => (
                                <li key={i} className="text-navy flex gap-2">
                                  <span className="text-crimson">→</span> {String(a)}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {r.snippet && (
                          <div>
                            <span className="font-medium text-navy text-xs uppercase tracking-wider">
                              Note
                            </span>
                            <p className="mt-1 text-muted whitespace-pre-wrap">{r.snippet}</p>
                          </div>
                        )}
                        {!actionItems.length && !r.snippet && (
                          <p className="text-muted italic">No additional detail.</p>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const DOMAIN_LABEL = { parts: 'Parts', other: 'Other' };

function PipelineTab() {
  const q = useQuery({
    queryKey: ['opportunities', 'open'],
    queryFn: () => api.get('/opportunities', { params: { status: 'open' } }).then(r => r.data.data),
  });
  const rows = q.data || [];

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted">All open opportunities across all domains.</p>
      <div className="bg-white border border-navy-100 rounded-lg shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-panel text-navy uppercase text-[10px] tracking-wide">
            <tr>
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">Customer</th>
              <th className="text-left px-3 py-2">Domain</th>
              <th className="text-left px-3 py-2">Stage</th>
              <th className="text-right px-3 py-2">Value</th>
              <th className="text-right px-3 py-2">Activity</th>
              <th className="text-right px-3 py-2">Close by</th>
              <th className="text-left px-3 py-2">Owner</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-navy-100">
            {rows.map(o => (
              <tr key={o.id} className="hover:bg-panel">
                <td className="px-3 py-1.5">
                  <Link to={`/pipeline/${o.id}`} className="text-navy hover:text-crimson font-medium">{o.name}</Link>
                </td>
                <td className="px-3 py-1.5">
                  {o.customer_id
                    ? <Link to={`/customers/${o.customer_id}`} className="text-navy hover:text-crimson">{o.customer_name}</Link>
                    : '—'}
                </td>
                <td className="px-3 py-1.5 text-xs text-muted">{DOMAIN_LABEL[o.domain] || o.domain || '—'}</td>
                <td className="px-3 py-1.5 capitalize">{o.stage}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{fmt$(o.value_usd) || '—'}</td>
                <td className="px-3 py-1.5 text-right text-xs text-muted">
                  {o.source_note_date ? new Date(o.source_note_date).toLocaleDateString() : '—'}
                </td>
                <td className="px-3 py-1.5 text-right text-xs text-muted">{o.expected_close_date || '—'}</td>
                <td className="px-3 py-1.5 text-xs text-muted">{o.user_name || '—'}</td>
              </tr>
            ))}
            {!q.isLoading && rows.length === 0 && (
              <tr><td colSpan={8} className="text-center text-muted py-6">No open opportunities.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'activity', label: 'Activity' },
  { id: 'pipeline', label: 'Pipeline' },
];

export default function RepActivity() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [tab,      setTab]      = useState('activity');
  const [period,   setPeriod]   = useState('30d');
  const [repId,    setRepId]    = useState('all');
  const [selected, setSelected] = useState(null); // "repId::week_start" or null
  const [ingestLog, setIngestLog] = useState(null);

  const ingestMutation = useMutation({
    mutationFn: () => api.post('/admin/call-reports/ingest', { days: 90 }).then(r => r.data),
    onSuccess: (data) => {
      setIngestLog(data.log);
      qc.invalidateQueries({ queryKey: ['reports', 'activity-log'] });
    },
    onError: (err) => {
      setIngestLog(err.response?.data?.log || err.message || 'Import failed');
    },
  });

  const { data, isLoading, isError } = useQuery({
    queryKey: ['reports', 'activity-log', period, repId],
    queryFn: () => api.get(`/reports/activity-log?period=${period}&rep_id=${repId}`).then(r => r.data),
    staleTime: 5 * 60_000,
  });

  // Filter detail rows when a summary row is selected
  const filteredDetail = useMemo(() => {
    if (!data?.detail) return [];
    if (!selected) return data.detail;
    const [selRepId, dayStart] = selected.split('::');
    const dayDate = dayStart.substring(0, 10); // YYYY-MM-DD
    return data.detail.filter(r =>
      String(r.rep_id) === selRepId &&
      r.date.substring(0, 10) === dayDate
    );
  }, [data, selected]);

  const selectedLabel = useMemo(() => {
    if (!selected || !data?.summary) return null;
    const [selRepId, dayStart] = selected.split('::');
    const row = data.summary.find(r => String(r.rep_id) === selRepId && r.day_start === dayStart);
    return row ? `${row.rep} — ${fmtDate(dayStart)}` : null;
  }, [selected, data]);

  return (
    <div className="space-y-5">
      <header>
        <div className="section-eyebrow">Reporting</div>
        <h1 className="text-2xl">Rep Activity</h1>
        <div className="rule-crimson" />
      </header>

      <div className="border-b border-panelDark flex gap-1">
        {TABS.map(t => (
          <button key={t.id}
            onClick={() => setTab(t.id)}
            className={clsx(
              'px-4 py-2 text-sm border-b-2 -mb-px',
              tab === t.id ? 'border-crimson text-crimson font-medium' : 'border-transparent text-navy hover:text-crimson'
            )}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'pipeline' && <PipelineTab />}

      {tab === 'activity' && <>
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Period */}
        <div className="flex gap-1 bg-panel rounded-lg p-1">
          {PERIODS.map(p => (
            <button key={p.key} onClick={() => { setPeriod(p.key); setSelected(null); }}
              className={clsx('px-3 py-1.5 rounded text-sm transition-colors',
                period === p.key ? 'bg-white text-navy font-medium shadow-sm' : 'text-muted hover:text-navy')}>
              {p.label}
            </button>
          ))}
        </div>

        {/* Rep filter */}
        {data?.reps?.length > 0 && (
          <select
            value={repId}
            onChange={e => { setRepId(e.target.value); setSelected(null); }}
            className="border border-navy-100 rounded px-3 py-1.5 text-sm text-navy bg-white">
            <option value="all">All reps</option>
            {data.reps.map(r => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
        )}

        {selected && (
          <button onClick={() => setSelected(null)}
            className="flex items-center gap-1.5 text-sm text-navy bg-panel border border-navy-100 rounded px-3 py-1.5 hover:bg-white transition-colors">
            <span className="font-medium">{selectedLabel}</span>
            <span className="text-muted">✕</span>
          </button>
        )}

        {user?.role === 'superadmin' && (
          <button
            onClick={() => { setIngestLog(null); ingestMutation.mutate(); }}
            disabled={ingestMutation.isPending}
            className="ml-auto flex items-center gap-1.5 text-sm border border-navy-100 rounded px-3 py-1.5 bg-white text-navy hover:bg-panel transition-colors disabled:opacity-50"
          >
            {ingestMutation.isPending ? '⏳ Importing…' : '⬇ Import call reports'}
          </button>
        )}
      </div>

      {ingestLog && (
        <pre className="text-xs bg-panel text-navy p-3 rounded-lg overflow-x-auto whitespace-pre-wrap max-h-40">
          {ingestLog}
        </pre>
      )}

      {isLoading && (
        <div className="space-y-2 animate-pulse">
          {[1,2,3,4].map(i => <div key={i} className="h-8 bg-navy-100 rounded" />)}
        </div>
      )}

      {isError && <p className="text-crimson text-sm">Failed to load activity data.</p>}

      {data && (
        <>
          {/* Daily summary */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-semibold text-navy">Daily Summary</h2>
              <span className="text-xs text-muted">Click a row to filter detail below</span>
            </div>
            <div className="bg-white border border-navy-100 rounded-lg p-4 shadow-sm">
              <SummaryTable
                rows={data.summary}
                selected={selected}
                onSelect={setSelected}
              />
            </div>
          </section>

          {/* Detail log */}
          <section>
            <div className="flex items-center gap-3 mb-2">
              <h2 className="font-semibold text-navy">Activity Detail</h2>
              {selected
                ? <span className="text-xs bg-crimson text-white px-2 py-0.5 rounded">
                    Filtered: {selectedLabel}
                  </span>
                : <span className="text-xs text-muted">
                    {filteredDetail.length} records
                    {filteredDetail.length === 500 && ' (limit 500 — narrow the period or rep filter)'}
                  </span>
              }
            </div>
            <div className="bg-white border border-navy-100 rounded-lg p-4 shadow-sm">
              <DetailTable rows={filteredDetail} />
            </div>
          </section>
        </>
      )}
      </>}
    </div>
  );
}
